import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { apiUrl } from '../runtime'
import { apiFetch } from './apiClient'
import {
  clearSession,
  getAccessToken,
  getStoredUser,
  setAccessToken,
  storeSession,
  type StoredUser,
} from './tokenStore'

export interface AuthState {
  user: StoredUser | null
  loading: boolean
}

export interface AuthContextValue extends AuthState {
  requestMagicLink: (
    email: string,
    redirectUrl?: string,
  ) => Promise<{ status: string; sent: boolean; login_url?: string | null }>
  logout: () => Promise<void>
}

interface MeResponse {
  id: number | null
  email: string
  name: string | null
  role: string
  is_admin: boolean
  advisor_code: string | null
}

interface TokenResponse {
  access_token: string
  token_type: 'bearer'
  expires_in: number
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchMeAndStore(token: string): Promise<StoredUser | null> {
  const response = await fetch(apiUrl('/api/auth/me'), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) return null
  const me = (await response.json()) as MeResponse
  storeSession(token, me)
  return getStoredUser()
}

async function exchangeUrlToken(
  path: string,
  body: Record<string, string>,
): Promise<StoredUser | null> {
  const tokenResponse = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!tokenResponse.ok) return null
  const data = (await tokenResponse.json()) as TokenResponse
  setAccessToken(data.access_token)
  return fetchMeAndStore(data.access_token)
}

async function silentRefresh(): Promise<StoredUser | null> {
  const tokenResponse = await fetch(apiUrl('/api/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
  })
  if (!tokenResponse.ok) return null
  const data = (await tokenResponse.json()) as TokenResponse
  setAccessToken(data.access_token)
  return fetchMeAndStore(data.access_token)
}

function stripUrlParam(name: string): void {
  let nextUrl: URL
  try {
    nextUrl = new URL(window.location.href)
  } catch {
    return
  }
  nextUrl.searchParams.delete(name)
  nextUrl.searchParams.delete('token') // legacy alias
  try {
    const relative = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
    window.history.replaceState({}, '', relative)
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }))
  } catch {
    // best-effort in environments without a real History API
  }
  // If window.location is a plain URL value (e.g. test override), mutate it in
  // place so subsequent reads of window.location.search reflect the cleaned URL.
  if (window.location instanceof URL) {
    window.location.searchParams.delete(name)
    window.location.searchParams.delete('token')
  }
}

async function bootstrapSession(): Promise<StoredUser | null> {
  const params = new URLSearchParams(window.location.search)
  const inviteToken = params.get('invite_token')
  const loginToken = params.get('login_token') || params.get('token')

  if (inviteToken) {
    stripUrlParam('invite_token')
    const user = await exchangeUrlToken('/api/auth/claim-invite', {
      invite_token: inviteToken,
    })
    if (user) return user
  }
  if (loginToken) {
    stripUrlParam('login_token')
    const user = await exchangeUrlToken('/api/auth/verify-magic-link', {
      token: loginToken,
    })
    if (user) return user
  }

  const stored = getStoredUser()
  const storedToken = getAccessToken()
  if (storedToken && stored) {
    const refreshed = await fetchMeAndStore(storedToken)
    if (refreshed) return refreshed
  }

  const viaRefresh = await silentRefresh()
  if (viaRefresh) return viaRefresh

  clearSession()
  return null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const cached = getStoredUser()
    return { user: cached, loading: cached === null }
  })

  useEffect(() => {
    let cancelled = false
    bootstrapSession()
      .catch(() => null)
      .then((user) => {
        if (!cancelled) setState({ user, loading: false })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const requestMagicLink = useCallback(
    async (email: string, redirectUrl?: string) => {
      const response = await fetch(apiUrl('/api/auth/magic-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          redirect_url: redirectUrl ?? `${window.location.origin}/login`,
        }),
      })
      return (await response.json()) as {
        status: string
        sent: boolean
        login_url?: string | null
      }
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // best-effort
    }
    clearSession()
    setState({ user: null, loading: false })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, requestMagicLink, logout }),
    [state, requestMagicLink, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
